import {Button, FlexRow} from '@forecast-it/design-system';
import styled, {css} from 'styled-components';

// The design system ships exactly one button size — see BaseButton's `size.height` token, and the
// absence of any `size` prop on Button. That size is right for a form or a page header and far too
// tall for the surfaces Nova lives on: a task row is a dense line among other chips, and a thread
// action sits inside an answer card it must not out-weigh.
//
// So the sizes below are this feature's own, and they are the ONLY thing taken away from the
// design system here. Colour, emphasis, focus ring, hover and pressed states, disabled treatment,
// icons and keyboard behaviour all stay the system's. These controls used to be hand-built pills
// that reimplemented all of that from raw tokens; this keeps the system's behaviour and overrides
// three properties of its geometry.
//
// Raised with the design-system team as the missing compact size. When it lands, delete these and
// pass the size prop instead.

// Height, padding and font are all set on the button element itself in BaseButton, so overriding
// them here lands on the same element rather than needing to reach into the label. min-width is
// the one that bites if forgotten: BaseButton defaults it to 100px, which in a task row leaves a
// Nova column of mostly empty button.
const compactGeometry = (height: number, padding: string, gap: number) => css`
	height: ${height}px;
	min-width: 0;
	padding: 0 ${padding};
	gap: ${gap}px;
	font-size: 12px;
	line-height: 16px;

	/* The icon keeps the design system's own small size rather than scaling with the button. */
	svg {
		width: 16px;
		height: 16px;
	}
`;

// Row and card controls, to sit among the other chips on a task line.
export const ROW_CONTROL_HEIGHT = 24;
// The task modal header and the thread's action rows: one step up, where the control is the thing
// being looked at rather than one of many on a line.
export const THREAD_CONTROL_HEIGHT = 28;

// A square button holds an icon alone — the agent caret, the stop control, the composer's send.
// Width is pinned to the height so it reads as a square half rather than a short wide one.
const squareGeometry = (size: number) => css`
	height: ${size}px;
	width: ${size}px;
	min-width: ${size}px;
	padding: 0;
	justify-content: center;

	svg {
		width: 16px;
		height: 16px;
	}
`;

export const RowButton = styled(Button)`
	${compactGeometry(ROW_CONTROL_HEIGHT, '8px', 4)}
`;

export const RowSquareButton = styled(Button)`
	${squareGeometry(ROW_CONTROL_HEIGHT)}
`;

export const HeaderButton = styled(Button)`
	${compactGeometry(THREAD_CONTROL_HEIGHT, '12px', 6)}
`;

export const HeaderSquareButton = styled(Button)`
	${squareGeometry(THREAD_CONTROL_HEIGHT)}
`;

export const ThreadButton = styled(Button)`
	${compactGeometry(THREAD_CONTROL_HEIGHT, '10px', 4)}
`;

// The collapsed thread's "Show" is the one control drawn smaller still: it is a line of running
// text that happens to be pressable, not an action on the answer.
export const InlineButton = styled(Button)`
	${compactGeometry(20, '8px', 4)}
`;

// SplitButton.Section is not usable here for two reasons, both of which cost more than the
// component saves: it is not a forwardRef, so the stop control could not take focus when a run
// starts; and it sets `data-cy="split-button-section"` after spreading its props, so it silently
// overwrites the test ids three Playwright specs and this feature's own suite select on.
//
// This is that component's own composition instead — the design system's FlexRow at the same
// `xxs` gap, with the end buttons' radii flattened where they meet. Both halves are still design
// system Buttons; only the two lines SplitButton would have contributed are restated here.
//
// Every state of the control renders this row, including the two that have only one half. A state
// that dropped the row and returned a bare button would change the shape of the tree, so React
// would unmount the button and mount a new one on the way in and out of that state - and a click
// already in flight when a run finishes would land on the node being replaced and be swallowed.
// Keeping the row constant keeps the first half the same DOM node across every transition.
// `flex: none`, not SplitButton's own `flex="0"`. FlexRow writes whatever it is given straight
// into the `flex` shorthand, and `flex: 0` expands to `0 1 0%` - a zero base width that is still
// allowed to shrink. The buttons inside carry `min-width: 0` (the design system's own 100px
// minimum is far too wide for a task row), so nothing holds the control open: in a flex cluster
// like a task row's leading cell it collapses to nothing and the column renders empty. `none` is
// `0 0 auto`, which sizes the row to its content and never lets it be squeezed.
export const SplitRow = styled(FlexRow).attrs({gap: 'xxs', flex: 'none'})`
	align-items: center;
`;

// While a run is going the main half shows a loader and the word Working. It is not pressable —
// there is nothing to press until the run ends — but it is not refused either, so it keeps its
// live emphasis and only drops the pointer.
export const runningButtonStyle = {cursor: 'default'} as const;

// The design system's loader animation sizes itself to its container, so it needs one.
export const LoaderSlot = styled.span<{$size: number}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: ${({$size}) => $size}px;
	height: ${({$size}) => $size}px;
	line-height: 0;
	overflow: hidden;

	> *,
	svg {
		width: 100%;
		height: 100%;
		display: block;
	}
`;
